"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { Plant } from "./PlantList";

/** The plant fields the spotlight card shows (outdoor `Plant` satisfies it). */
export type LoaderPlant = Pick<
  Plant,
  "id" | "commonName" | "scientificName" | "sunLevel"
> &
  Partial<
    Pick<Plant, "imageUrl" | "waterNeeds" | "bloomTime" | "bloomColor">
  > & {
    /** Container flow: "thriller" | "filler" | "spiller", shown on the card. */
    role?: string;
    /** Container flow: how many go in the pot. */
    count?: number;
  };

interface GenerationLoaderProps {
  plants: LoaderPlant[];
  recipeName?: string;
  uploadedImageUrl?: string;
  // Backend progress (0-100) - if provided, overrides timer-based progress
  progress?: number;
  // Job status info
  status?: "queued" | "processing" | "rate_limited" | "retrying";
  queuePosition?: number;
  estimatedWaitSeconds?: number;
  // Whether all four seasons are being generated (default) or Summer only
  allSeasons?: boolean;
  /** Container flow: one render of a planted pot (~30s) with its own phases,
   *  imagery and tips. `uploadedImageUrl` is then the planter photo. */
  variant?: "outdoor" | "container";
}

const ALL_SEASONS_PHASES = [
  {
    maxTime: 15,
    label: "Analyzing your garden space...",
    gif: "/Gardening_GIF/Shovel.gif",
  },
  {
    maxTime: 35,
    label: "Placing your plants in the design...",
    gif: "/Gardening_GIF/Plant.gif",
  },
  {
    maxTime: 55,
    label: "Rendering your Summer garden...",
    gif: "/Gardening_GIF/Sun.gif",
  },
  {
    maxTime: 75,
    label: "Rendering your Spring garden...",
    gif: "/Gardening_GIF/Flower.gif",
  },
  {
    maxTime: 95,
    label: "Painting the Autumn colors...",
    gif: "/Gardening_GIF/Fruit_Tree.gif",
  },
  {
    maxTime: 110,
    label: "Creating your Winter scene...",
    gif: "/Nature_GIF/Tree_01.gif",
  },
  {
    maxTime: Infinity,
    label: "Adding finishing touches...",
    gif: "/Gardening_GIF/Water_can.gif",
  },
];

const SUMMER_ONLY_PHASES = [
  {
    maxTime: 12,
    label: "Analyzing your garden space...",
    gif: "/Gardening_GIF/Shovel.gif",
  },
  {
    maxTime: 30,
    label: "Placing your plants in the design...",
    gif: "/Gardening_GIF/Plant.gif",
  },
  {
    maxTime: 50,
    label: "Rendering your garden design...",
    gif: "/Gardening_GIF/Sun.gif",
  },
  {
    maxTime: Infinity,
    label: "Adding finishing touches...",
    gif: "/Gardening_GIF/Water_can.gif",
  },
];

// Backend-mode phases — keyed by displayed PROGRESS (not time). Used only when
// the worker reports real progress. The three non-summer seasons now render in
// parallel, so labels are milestone-based rather than sequential per-season.
const ALL_SEASONS_BACKEND_PHASES = [
  {
    maxProgress: 15,
    label: "Analyzing your garden space...",
    gif: "/Gardening_GIF/Shovel.gif",
  },
  {
    maxProgress: 20,
    label: "Placing your plants in the design...",
    gif: "/Gardening_GIF/Plant.gif",
  },
  {
    maxProgress: 40,
    label: "Rendering your garden design...",
    gif: "/Gardening_GIF/Sun.gif",
  },
  {
    maxProgress: 60,
    label: "Polishing your Summer garden...",
    gif: "/Gardening_GIF/Flower.gif",
  },
  {
    maxProgress: 90,
    label: "Painting Spring, Autumn & Winter...",
    gif: "/Gardening_GIF/Fruit_Tree.gif",
  },
  {
    maxProgress: Infinity,
    label: "Adding finishing touches...",
    gif: "/Gardening_GIF/Water_can.gif",
  },
];

const SUMMER_ONLY_BACKEND_PHASES = [
  {
    maxProgress: 15,
    label: "Analyzing your garden space...",
    gif: "/Gardening_GIF/Shovel.gif",
  },
  {
    maxProgress: 20,
    label: "Placing your plants in the design...",
    gif: "/Gardening_GIF/Plant.gif",
  },
  {
    maxProgress: 60,
    label: "Rendering your garden design...",
    gif: "/Gardening_GIF/Sun.gif",
  },
  {
    maxProgress: Infinity,
    label: "Adding finishing touches...",
    gif: "/Gardening_GIF/Water_can.gif",
  },
];

// Container flow: a single render of the planted pot. Timer-paced only (the
// container render is one synchronous call, there is no worker progress).
const CONTAINER_PHASES = [
  {
    maxTime: 6,
    label: "Measuring your planter...",
    gif: "/Gardening_GIF/Bucket.gif",
  },
  {
    maxTime: 12,
    label: "Mixing the potting soil...",
    gif: "/Gardening_GIF/Shovel.gif",
  },
  {
    maxTime: 20,
    label: "Placing your thriller, fillers & spillers...",
    gif: "/Gardening_GIF/Plant.gif",
  },
  {
    maxTime: 30,
    label: "Rendering your container garden...",
    gif: "/Gardening_GIF/Flower.gif",
  },
  {
    maxTime: Infinity,
    label: "Watering it in...",
    gif: "/Gardening_GIF/Water_can.gif",
  },
];

// Estimated total generation time (seconds) used for timer-based progress
const ALL_SEASONS_DURATION = 120;
const SUMMER_ONLY_DURATION = 60;
const CONTAINER_DURATION = 35;

// Where we remember how long recent generations actually took, so the timer
// fallback can pace itself to reality instead of a fixed guess. Keyed by mode.
const DURATION_STORE_KEYS = {
  all: "plantista:genDurations:all",
  summer: "plantista:genDurations:summer",
  container: "plantista:genDurations:container",
};

type DurationMode = keyof typeof DURATION_STORE_KEYS;
const modeOf = (allSeasons: boolean | "container"): DurationMode =>
  allSeasons === "container" ? "container" : allSeasons ? "all" : "summer";
const DEFAULT_DURATIONS: Record<DurationMode, number> = {
  all: ALL_SEASONS_DURATION,
  summer: SUMMER_ONLY_DURATION,
  container: CONTAINER_DURATION,
};

/** Record how long a generation actually took so future loaders pace themselves
 *  to reality. Keeps the last 5 per mode; pass "container" for the container flow. */
export function recordGenerationDuration(
  allSeasons: boolean | "container",
  seconds: number
) {
  if (seconds < 5) return; // sanity: ignore implausibly fast runs
  if (typeof window === "undefined") return;
  const key = DURATION_STORE_KEYS[modeOf(allSeasons)];
  try {
    const raw = window.localStorage.getItem(key);
    const arr: number[] = raw ? JSON.parse(raw) : [];
    const next = [...(Array.isArray(arr) ? arr : []), seconds].slice(-5);
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // SSR / quota / disabled storage — non-fatal, just skip recording
  }
}

/** Median of recently recorded durations for the mode, or the default estimate
 *  when nothing usable is stored. */
function readAdaptiveDuration(allSeasons: boolean | "container"): number {
  const mode = modeOf(allSeasons);
  const fallback = DEFAULT_DURATIONS[mode];
  if (typeof window === "undefined") return fallback;
  const key = DURATION_STORE_KEYS[mode];
  try {
    const raw = window.localStorage.getItem(key);
    const arr: number[] = raw ? JSON.parse(raw) : [];
    const nums = (Array.isArray(arr) ? arr : []).filter(
      (n) => typeof n === "number" && n > 0
    );
    if (nums.length === 0) return fallback;
    const sorted = [...nums].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];
  } catch {
    return fallback;
  }
}

const GARDENING_TIPS = [
  "Water new plants deeply for the first 2 weeks after planting",
  "Mulch retains moisture and keeps roots cool in summer",
  "Group plants with similar sun and water needs together",
  "Morning is the best time to water — less evaporation",
  "Native plants attract pollinators and need less maintenance",
  "Add compost to improve soil drainage and nutrients",
  "Space plants according to their mature size, not nursery pot size",
  "Deadhead spent blooms to encourage more flowers",
  "A 2-3 inch layer of mulch saves water and suppresses weeds",
  "Newly planted shrubs need about 1 inch of water per week",
];

const CONTAINER_TIPS = [
  "Use potting mix, never garden soil — it compacts and drowns roots in a pot",
  "Water until it runs out of the drainage holes, then let the top inch dry",
  "Pots dry out fast in summer heat — check them every morning",
  "Plant the thriller first, then fillers around it, spillers at the rim",
  "Leave an inch between the soil and the rim so water doesn't spill over",
  "Feed every 2–3 weeks: frequent watering washes nutrients out of a pot",
  "Group pots together to shade each other's roots on hot days",
  "Lift heavy planters onto pot feet so they drain freely",
  "Pinch back fillers early to keep the arrangement full and bushy",
  "Turn the pot a quarter every week or two so it grows evenly toward the light",
];

const ROLE_LABEL: Record<string, string> = {
  thriller: "Thriller",
  filler: "Filler",
  spiller: "Spiller",
};

function formatSunLevel(sunLevel: string) {
  return sunLevel
    .replace(/_/g, " ")
    .replace("PART SUN PART SHADE", "Part Sun")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function GenerationLoader({
  plants,
  recipeName,
  uploadedImageUrl,
  progress: backendProgress,
  status,
  queuePosition,
  estimatedWaitSeconds,
  allSeasons = true,
  variant = "outdoor",
}: GenerationLoaderProps) {
  const isContainer = variant === "container";
  const mode = isContainer ? "container" : allSeasons;
  const phases = isContainer
    ? CONTAINER_PHASES
    : allSeasons
      ? ALL_SEASONS_PHASES
      : SUMMER_ONLY_PHASES;
  const tips = isContainer ? CONTAINER_TIPS : GARDENING_TIPS;
  // Default duration the phase thresholds are calibrated against (120s / 60s / 35s).
  const defaultDuration = DEFAULT_DURATIONS[modeOf(mode)];
  // Adaptive estimate D — median of recent real runs, read once (not per render).
  const [estimatedDuration] = useState(() => readAdaptiveDuration(mode));
  const [elapsed, setElapsed] = useState(0);
  const [plantIndex, setPlantIndex] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const [plantFade, setPlantFade] = useState(true);
  const [tipFade, setTipFade] = useState(true);

  // Smoothed displayed progress (backend mode only). The worker reports coarse
  // milestones with uneven pacing (long stall at the summer render, then a
  // rapid burst as the parallel seasons finish); we ease `displayed` toward the
  // real value so the bar keeps moving steadily without crawling or leaping.
  const [displayed, setDisplayed] = useState(0);
  // Last backend value we reacted to + when we saw it, and an EMA of backend
  // velocity in %/second — used to trickle the bar during milestone stalls.
  const lastBackendRef = useRef(0);
  const lastBackendTimeRef = useRef<number | null>(null);
  const velocityRef = useRef(0.3);

  // Elapsed timer (1s tick)
  useEffect(() => {
    const interval = setInterval(() => setElapsed((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // Track backend velocity: whenever the reported value changes, fold the
  // instantaneous velocity into an EMA so the trickle rate adapts to the job.
  useEffect(() => {
    if (backendProgress === undefined) return;
    if (backendProgress !== lastBackendRef.current) {
      const now = Date.now();
      const lastTime = lastBackendTimeRef.current;
      const dt = Math.max(0.001, (now - (lastTime ?? now)) / 1000);
      const v = (backendProgress - lastBackendRef.current) / dt;
      // Skip the EMA fold on the very first observation (no baseline time yet).
      if (v > 0 && lastTime !== null) {
        velocityRef.current = velocityRef.current * 0.7 + v * 0.3;
      }
      lastBackendRef.current = backendProgress;
      lastBackendTimeRef.current = now;
    }
  }, [backendProgress]);

  // Ease `displayed` toward the backend value on a 200ms tick (backend mode).
  useEffect(() => {
    if (backendProgress === undefined) return;
    const dt = 0.2; // seconds per tick
    const interval = setInterval(() => {
      setDisplayed((d) => {
        const backend = backendProgress;
        // Lookahead ceiling: keep moving during stalls, but never race ahead
        // of the real value or fake completion.
        const T = backend >= 100 ? 100 : Math.min(backend + 15, 97);
        // Behind the backend: catch up fast but smoothly. Otherwise trickle at
        // the adaptive velocity, scaled by remaining headroom so the bar slows
        // asymptotically toward the ceiling instead of hard-stopping at it.
        const r =
          d < backend
            ? Math.max(2, (backend - d) * 1.2)
            : Math.max(0.15, Math.min(2, velocityRef.current)) *
              Math.max(0.05, (T - d) / 15);
        const next = Math.min(T, d + r * dt);
        return next > d ? next : d;
      });
    }, 200);
    return () => clearInterval(interval);
  }, [backendProgress]);

  // Plant carousel (6s rotation with fade)
  const rotatePlant = useCallback(() => {
    if (plants.length <= 1) return;
    setPlantFade(false);
    setTimeout(() => {
      setPlantIndex((i) => (i + 1) % plants.length);
      setPlantFade(true);
    }, 300);
  }, [plants.length]);

  useEffect(() => {
    if (plants.length <= 1) return;
    const interval = setInterval(rotatePlant, 6000);
    return () => clearInterval(interval);
  }, [rotatePlant, plants.length]);

  // Tip rotation (8s)
  const rotateTip = useCallback(() => {
    setTipFade(false);
    setTimeout(() => {
      setTipIndex((i) => (i + 1) % tips.length);
      setTipFade(true);
    }, 300);
  }, [tips.length]);

  useEffect(() => {
    const interval = setInterval(rotateTip, 8000);
    return () => clearInterval(interval);
  }, [rotateTip]);

  // Displayed progress: smoothed value in backend mode, timer-based otherwise.
  // Timer mode uses an asymptotic curve (not linear) so a wrong estimate
  // degrades gracefully — reaches ~89% at t = D, ~67% at half the estimate,
  // and never crawls if the run finishes much faster than expected.
  const progress =
    backendProgress !== undefined
      ? Math.round(displayed)
      : Math.min(
          95,
          Math.round(
            100 * (1 - Math.exp(-elapsed / (0.45 * estimatedDuration)))
          )
        );

  // Current progress phase. Backend mode selects milestone-based phases keyed
  // by progress; timer mode uses the calibrated time thresholds, with elapsed
  // scaled to the default reference duration so an adaptive D keeps the labels
  // in sync with the (default-calibrated) maxTime cutoffs.
  let phase;
  if (backendProgress !== undefined) {
    const backendPhases = allSeasons
      ? ALL_SEASONS_BACKEND_PHASES
      : SUMMER_ONLY_BACKEND_PHASES;
    phase =
      backendPhases.find((p) => progress < p.maxProgress) ||
      backendPhases[backendPhases.length - 1];
  } else {
    const scaledElapsed = elapsed * (defaultDuration / estimatedDuration);
    phase =
      phases.find((p) => scaledElapsed < p.maxTime) ||
      phases[phases.length - 1];
  }

  // Queue status message
  let statusMessage = phase.label;
  if (status === "queued" && queuePosition !== undefined) {
    statusMessage = `You're #${queuePosition} in the queue...`;
  } else if (status === "rate_limited") {
    statusMessage = "Waiting for available resources...";
  } else if (status === "retrying") {
    statusMessage = "Retrying generation, almost there...";
  } else if (elapsed >= estimatedDuration && backendProgress === undefined) {
    statusMessage = "Almost there...";
  }

  // Format estimated wait time
  const formatWaitTime = (seconds: number) => {
    if (seconds < 60) return `~${seconds}s`;
    const minutes = Math.ceil(seconds / 60);
    return `~${minutes} min`;
  };

  const currentPlant = plants[plantIndex];
  // Container cards carry per-species counts; the pot holds their sum.
  const plantTotal = isContainer
    ? plants.reduce((n, p) => n + (p.count ?? 1), 0)
    : plants.length;

  return (
    <div
      className="flex flex-col items-center justify-center py-12 px-4 bg-plantista-100 dark:bg-plantista-900 rounded-3xl shadow-xl border border-plantista-200 dark:border-plantista-800 max-w-2xl mx-auto"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {/* Progress phase with GIF */}
      <div className="flex flex-col items-center gap-3 mb-8">
        <Image
          src={phase.gif}
          alt=""
          aria-hidden="true"
          width={64}
          height={64}
          unoptimized
          className="w-16 h-16"
        />
        <p className="text-lg font-semibold text-plantista-950 dark:text-plantista-50 font-display text-center">
          {statusMessage}
        </p>
        {estimatedWaitSeconds !== undefined && estimatedWaitSeconds > 0 && (
          <p className="text-sm text-plantista-600 dark:text-plantista-400">
            Estimated wait: {formatWaitTime(estimatedWaitSeconds)}
          </p>
        )}

        {/* Progress bar */}
        <div
          className="w-64 sm:w-80 h-3 bg-plantista-200 dark:bg-plantista-700 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Design generation progress"
        >
          <div
            className="h-full bg-green-500 rounded-full transition-all duration-200 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-xs text-plantista-600 dark:text-plantista-400">
          {progress}%
        </span>
      </div>

      {/* Plant spotlight carousel */}
      {currentPlant && (
        <div
          className={`flex items-center gap-5 bg-white dark:bg-plantista-800 rounded-2xl p-5 mb-8 w-full max-w-md shadow-sm border border-plantista-200 dark:border-plantista-700 transition-opacity duration-300 ${
            plantFade ? "opacity-100" : "opacity-0"
          }`}
        >
          {currentPlant.imageUrl && (
            <Image
              src={currentPlant.imageUrl}
              alt={currentPlant.commonName}
              width={120}
              height={120}
              className="w-[120px] h-[120px] rounded-xl object-cover flex-shrink-0"
              unoptimized
            />
          )}
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className="text-base font-bold text-plantista-950 dark:text-plantista-50 truncate">
              {currentPlant.commonName}
            </h3>
            {currentPlant.role && (
              <span className="w-fit rounded-full bg-plantista-100 px-2 py-0.5 text-xs font-semibold text-plantista-700 dark:bg-plantista-700 dark:text-plantista-200">
                {currentPlant.count ? `${currentPlant.count} · ` : ""}
                {ROLE_LABEL[currentPlant.role] ?? currentPlant.role}
              </span>
            )}
            <p className="text-sm italic text-plantista-600 dark:text-plantista-400 truncate">
              {currentPlant.scientificName}
            </p>
            <div className="flex flex-col gap-0.5 mt-1 text-sm text-plantista-700 dark:text-plantista-300">
              <span>☀️ {formatSunLevel(currentPlant.sunLevel)}</span>
              {currentPlant.waterNeeds && (
                <span>💧 {currentPlant.waterNeeds}</span>
              )}
              {currentPlant.bloomTime && (
                <span>
                  🌸 {currentPlant.bloomTime}
                  {currentPlant.bloomColor
                    ? ` · ${currentPlant.bloomColor}`
                    : ""}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gardening tip */}
      <div
        className={`bg-white dark:bg-plantista-800 rounded-xl px-5 py-4 mb-6 w-full max-w-md shadow-sm border border-plantista-200 dark:border-plantista-700 transition-opacity duration-300 ${
          tipFade ? "opacity-100" : "opacity-0"
        }`}
      >
        <p className="text-sm text-plantista-800 dark:text-plantista-200">
          <span className="mr-1.5">💡</span>
          {tips[tipIndex % tips.length]}
        </p>
      </div>

      {/* Summary line */}
      <div className="flex items-center gap-2 text-xs text-plantista-600 dark:text-plantista-400">
        {uploadedImageUrl && (
          <Image
            src={uploadedImageUrl}
            alt={isContainer ? "Your planter" : "Your garden"}
            width={24}
            height={24}
            className={`w-6 h-6 rounded ${isContainer ? "object-contain bg-white" : "object-cover"}`}
            unoptimized
          />
        )}
        <span>
          {plantTotal} plant{plantTotal !== 1 ? "s" : ""}
          {recipeName ? ` · ${recipeName}` : ""}
        </span>
      </div>
    </div>
  );
}
